import React, { useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormHelperText, Box } from '@mui/material';
import { Button } from '@totalsoft/rocket-ui';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import {
  shape,
  required,
  concatFailure,
  isValid,
  getErrors,
  Validator,
  Failure,
  Success,
  ValidationError,
  errorMessage
} from '@totalsoft/pure-validations';
import { isDirty } from '@totalsoft/change-tracking';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from 'hooks/apollo';
import { RAW_FINANCIAL_INDICATORS_QUERY } from '../../../../financialIndicators/components/list/queries';
import { ADD_UPDATE_ACCOUNT_STATEMENT_INDICATOR } from '../../mutations';
import AccountStatementIndicatorsModalEditComponent from './AccountStatementIndicatorsModalEditComponent';
import { accountStatementIndicatorModalVar } from './variables';

// #region validator

const denyMultipleDetailsWithSameInfoIndicator = indicators =>
  Validator(content => {
    if (!content?.name || !content.code || !content.computeOrder || !indicators) return Success;

    if (indicators.some(indicator => indicator.code === content.code && indicator.statementIndicatorId !== content.statementIndicatorId))
      return Failure(ValidationError('AccountStatement.Indicators.Validators.CodeUnique'));
    if (indicators.some(indicator => indicator.name === content.name && indicator.statementIndicatorId !== content.statementIndicatorId))
      return Failure(ValidationError('AccountStatement.Indicators.Validators.NameUnique'));
    if (
      indicators.some(
        indicator => indicator.computeOrder === content.computeOrder && indicator.statementIndicatorId !== content.statementIndicatorId
      )
    )
      return Failure(ValidationError('AccountStatement.Indicators.Validators.ComputeOrderUnique'));
    return Success;
  });

const statementIndicatorValidator = indicators =>
  [
    shape({
      code: concatFailure(required |> errorMessage('AccountStatement.Indicators.Validators.CodeRequired')),
      name: required |> errorMessage('AccountStatement.Indicators.Validators.NameRequired'),
      expression: required |> errorMessage('AccountStatement.Indicators.Validators.ExpressionRequired'),
      computeOrder: required |> errorMessage('AccountStatement.Indicators.Validators.ComputeOrderRequired')
    }),
    denyMultipleDetailsWithSameInfoIndicator(indicators)
  ] |> concatFailure;
// #endregion

const AccountStatementIndicatorsModalEditContainer = ({ indicator, configId, indicators }) => {
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const [statementIndicatorLens, dirtyInfo] = useChangeTrackingLens(indicator);
  const entityIsDirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  const { data: financialIndicatorData } = useQuery(RAW_FINANCIAL_INDICATORS_QUERY);
  const financialIndicatorList = useMemo(() => financialIndicatorData?.financialIndicators || [], [financialIndicatorData]);

  const indicatorValidator = useMemo(() => statementIndicatorValidator(indicators), [indicators]);
  const [validation, validate] = useDirtyFieldValidation(indicatorValidator);

  useEffect(() => {
    validate(statementIndicatorLens |> get, dirtyInfo);
  }, [dirtyInfo, statementIndicatorLens, validate]);

  const handleClose = useCallback(() => {
    accountStatementIndicatorModalVar({ open: false });
  }, []);

  const [saveAccountStatementIndicator] = useMutation(ADD_UPDATE_ACCOUNT_STATEMENT_INDICATOR, {
    onCompleted: _ => setSaving(false),
    onError: () => {
      setSaving(false);
    }
  });
  const handleSave = useCallback(() => {
    if (!validate(statementIndicatorLens |> get)) {
      return;
    }
    saveAccountStatementIndicator({
      variables: {
        input: {
          statementIndicatorId: parseInt(statementIndicatorLens.statementIndicatorId |> get),
          configId: parseInt(configId),
          code: statementIndicatorLens.code |> get,
          name: statementIndicatorLens.name |> get,
          attributeName: statementIndicatorLens.attributeName |> get,
          expression: statementIndicatorLens.expression |> get,
          financialIndicatorId: parseInt(statementIndicatorLens.financialIndicatorId |> get),
          orderNo: parseInt(statementIndicatorLens.orderNo |> get),
          computeOrder: parseInt(statementIndicatorLens.computeOrder |> get),
          readOnlyYN: statementIndicatorLens.readOnlyYN |> get,
          visibleYN: statementIndicatorLens.visibleYN |> get
        }
      }
    });
  }, [configId, saveAccountStatementIndicator, validate, statementIndicatorLens]);

  return (
    <Box sx={{ p: 1 }}>
      <Prompt when={entityIsDirty} message={t('LeavingWithoutSaving')} />
      <AccountStatementIndicatorsModalEditComponent
        indicatorLens={statementIndicatorLens}
        validation={validation}
        financialIndicatorList={financialIndicatorList}
      />
      <Grid sx={{ mt: 1 }}>
        {!isValid(validation) && isDirty(dirtyInfo) && (
          <FormHelperText error={true} id='name-error-text'>
            {getErrors(validation)}
          </FormHelperText>
        )}
      </Grid>
      <Grid container item xs={12} spacing={2} justifyContent='flex-end'>
        <Button onClick={handleSave} disabled={saving} sx={{ mr: 1 }}>
          {saving ? t('Saving') : t('Save')}
        </Button>
        <Button onClick={handleClose} disabled={saving}>
          {t('Close')}
        </Button>
      </Grid>
    </Box>
  );
};

AccountStatementIndicatorsModalEditContainer.propTypes = {
  indicator: PropTypes.object.isRequired,
  configId: PropTypes.number.isRequired,
  indicators: PropTypes.array.isRequired
};

export default AccountStatementIndicatorsModalEditContainer;
